import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trophy, Timer, Users, ChevronUp, ChevronDown, Crown } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'react-hot-toast';
import PassportConnectButton from '../../PassportConnectButton';

const PredictionSection = ({
  prediction,
  predictionLoading,
  predictionResult,
  showPredictionForm,
  isStreamCreator,
  user,
  onCreatePrediction,
  onVotePrediction,
  onEndPrediction,
  onShowForm,
  onHideForm,
  onClearPredictionResult
}) => {
  // Add local voting state to show loading indicator
  const [votingOption, setVotingOption] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [timeLeft, setTimeLeft] = useState('0:00');
  const [selectedOption, setSelectedOption] = useState(null);
  const [showConfirmEnd, setShowConfirmEnd] = useState(false);
  const [winningOption, setWinningOption] = useState(null);

  // Debug logging (removed to reduce console noise)
  // console.log('PredictionSection props:', {
  //   isStreamCreator,
  //   hasPrediction: !!prediction,
  //   hasPredictionResult: !!predictionResult,
  //   onClearPredictionResultExists: !!onClearPredictionResult
  // });

  const totalVotes = prediction ?
    (prediction.option1Votes || 0) + (prediction.option2Votes || 0) : 0;

  const calculatePercentage = (votes) => {
    if (!totalVotes) return 0;
    return Math.round((votes / totalVotes) * 100);
  };

  const calculateTimeLeft = (endsAt) => {
    const now = new Date();
    const end = new Date(endsAt);

    if (!endsAt || isNaN(end.getTime())) {
      return '0:00';
    }

    const diff = end - now;

    if (diff <= 0) return '0:00';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  useEffect(() => {
    let intervalId;

    if (prediction?.status === 'active' && prediction.endsAt) {
      // Initial calculation
      setTimeLeft(calculateTimeLeft(prediction.endsAt));

      // Update every second
      intervalId = setInterval(() => {
        setTimeLeft(calculateTimeLeft(prediction.endsAt));
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [prediction?.status, prediction?.endsAt]);

  const handleVote = (option) => {
    if (!user?.isConnected) {
      toast.error('Please login to vote on predictions');
      return;
    }

    // Check if prediction is active
    if (prediction.status !== 'active') {
      toast.error('This prediction is no longer active');
      return;
    }

    // Check if user has already voted
    if (prediction.userVote) {
      toast.error('You have already voted on this prediction');
      return;
    }

    // Check if already voting
    if (votingOption) {
      return; // Prevent multiple clicks while voting
    }

    // Set local state for UI feedback
    setSelectedOption(option);
    setVotingOption(option);

    // Call the parent component's vote handler
    onVotePrediction(option)
      .then(() => {
        // Reset voting state on success (the parent component will update the prediction)
        setVotingOption(null);
      })
      .catch(() => {
        // Reset voting state and selection on error
        setVotingOption(null);
        setSelectedOption(null);
      });
  };

  const handleEndPrediction = (winner) => {
    setShowConfirmEnd(false);
    onEndPrediction(winner);
  };

  return (
    <div className="relative">
      <motion.div
        initial={false}
        animate={{ height: expanded ? 'auto' : '48px' }}
        transition={{ duration: 0.3 }}
        className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800"
      >
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-purple-400" />
            <span className="font-semibold text-white">
              {prediction ? 'Active Prediction' : 'Predictions'}
            </span>
            {prediction?.status === 'active' && (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-sm">
                Live
              </span>
            )}
          </div>
          {expanded ?
            <ChevronUp className="w-5 h-5 text-gray-400" /> :
            <ChevronDown className="w-5 h-5 text-gray-400" />
          }
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {predictionLoading ? (
                <div className="p-4">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-700 rounded"></div>
                      <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              ) : predictionResult ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="p-4 space-y-4"
                >
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center justify-center"
                  >
                    <Crown className="w-12 h-12 text-yellow-500" />
                  </motion.div>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-center space-y-2"
                  >
                    <h3 className="text-2xl font-bold text-white">
                      Prediction Complete!
                    </h3>
                    <p className="text-lg text-green-400 font-semibold">
                      {predictionResult[predictionResult.winner]} Wins!
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="bg-gray-800/50 rounded-lg p-4 space-y-2"
                  >
                    <div className="flex justify-between text-gray-400">
                      <span>Total Votes:</span>
                      <span>{predictionResult.totalVotes}</span>
                    </div>
                    {predictionResult.incentiveAmount && predictionResult.incentiveType && (
                      <div className="flex justify-between text-purple-400">
                        <span>Total Pool:</span>
                        <span>{predictionResult.incentiveAmount} {predictionResult.incentiveType}</span>
                      </div>
                    )}
                    {predictionResult.incentiveType && predictionResult.winningPoints !== null && predictionResult.isWinner && (
                      <div className="flex justify-between text-green-400">
                        <span>You Won:</span>
                        <span>{predictionResult.winningPoints} {predictionResult.incentiveType}</span>
                      </div>
                    )}
                    {predictionResult.incentiveType && !predictionResult.isWinner && predictionResult.userVote && (
                      <div className="flex justify-between text-red-400">
                        <span>Better luck next time!</span>
                        <span>0 {predictionResult.incentiveType}</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Clear button for stream creator */}
                  {isStreamCreator && onClearPredictionResult && (
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8 }}
                      className="mt-4"
                    >
                      <button
                        onClick={onClearPredictionResult}
                        className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Clear Prediction & Create New
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ) : prediction && !predictionResult ? (
                <div className="p-4 space-y-4">
                  {/* Question */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white">
                      {prediction.question}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Timer className="w-4 h-4" />
                        <span>{timeLeft}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{totalVotes} votes</span>
                      </div>
                    </div>
                  </div>

                  {/* Rewards Display */}
                  <PredictionRewards prediction={prediction} />

                  {/* Options */}
                  <div className="space-y-3">
                    {['option1', 'option2'].map((optionKey, index) => {
                      const votes = prediction[`${optionKey}Votes`] || 0;
                      const percentage = calculatePercentage(votes);
                      const isWinner = prediction.winner === prediction[optionKey];
                      const hasVoted = prediction.userVote === optionKey;

                      return (
                        <div key={optionKey} className="relative">
                          <button
                            onClick={() => handleVote(optionKey)}
                            disabled={prediction.status !== 'active' || prediction.userVote || votingOption !== null}
                            className={`w-full p-3 rounded-lg relative overflow-hidden transition-all
                              ${prediction.status === 'active' && !prediction.userVote && !votingOption
                                ? 'hover:ring-2 hover:ring-purple-500 cursor-pointer'
                                : 'cursor-default'}
                              ${hasVoted ? 'ring-2 ring-purple-500' : ''}
                              ${isWinner ? 'ring-2 ring-green-500' : ''}
                              ${votingOption === optionKey ? 'ring-2 ring-purple-300 animate-pulse' : ''}`}
                          >
                            {/* Background fill */}
                            <div
                              className={`absolute inset-0 transition-all duration-500
                                ${index === 0 ? 'bg-blue-500/20' : 'bg-pink-500/20'}
                                ${isWinner ? 'bg-green-500/20' : ''}`}
                              style={{ width: `${percentage}%` }}
                            />

                            {/* Content */}
                            <div className="relative flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {isWinner && (
                                  <Crown className="w-5 h-5 text-yellow-500" />
                                )}
                                {votingOption === optionKey ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-t-2 border-purple-500 rounded-full animate-spin"></div>
                                    <span className="font-medium text-white">Voting...</span>
                                  </div>
                                ) : (
                                  <span className="font-medium text-white">
                                    {prediction[optionKey]}
                                  </span>
                                )}
                                {hasVoted && (
                                  <span className="text-sm text-purple-400">
                                    • Your vote
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-medium text-gray-300">
                                {percentage}%
                              </span>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Creator Controls */}
                  {isStreamCreator && prediction.status === 'active' && (
                    <div className="pt-4 border-t border-gray-800">
                      {showConfirmEnd ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-400">
                            Select the winning option:
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEndPrediction('option1')}
                              className="flex-1 px-3 py-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
                            >
                              {prediction.option1}
                            </button>
                            <button
                              onClick={() => handleEndPrediction('option2')}
                              className="flex-1 px-3 py-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
                            >
                              {prediction.option2}
                            </button>
                          </div>
                          <button
                            onClick={() => setShowConfirmEnd(false)}
                            className="w-full px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowConfirmEnd(true)}
                          className="w-full px-3 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-colors"
                        >
                          End Prediction
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4">
                  {isStreamCreator ? (
                    user?.isConnected ? (
                      showPredictionForm ? (
                        <PredictionForm
                          onSubmit={onCreatePrediction}
                          onCancel={onHideForm}
                        />
                      ) : (
                        <button
                          onClick={onShowForm}
                          className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Trophy className="w-5 h-5" />
                          Create Prediction
                        </button>
                      )
                    ) : (
                      <div className="text-center space-y-3">
                        <p className="text-gray-400">
                          Login to create predictions
                        </p>
                        <PassportConnectButton className="w-full" />
                      </div>
                    )
                  ) : (
                    <p className="text-gray-400 text-center">
                      No active predictions at the moment
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const PredictionForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    question: '',
    option1: '',
    option2: '',
    duration: 300, // 5 minutes default
    incentiveType: null,
    incentiveAmount: ''
  });

  const durations = [
    { value: 300, label: '5 minutes' },
    { value: 600, label: '10 minutes' },
    { value: 900, label: '15 minutes' },
    { value: 1800, label: '30 minutes' },
    { value: 3600, label: '1 hour' },
    { value: 432000, label: '5 days' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate the incentive amount if present
    if (formData.incentiveType && formData.incentiveAmount) {
      const amount = parseFloat(formData.incentiveAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error(`Invalid amount: ${formData.incentiveAmount} is not a valid number`);
        return;
      }
      // Use the numeric value
      const validatedFormData = {
        ...formData,
        incentiveAmount: amount
      };
      onSubmit(validatedFormData);
    } else {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Question
        </label>
        <input
          type="text"
          value={formData.question}
          onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="Who will win the match?"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Option 1
          </label>
          <input
            type="text"
            value={formData.option1}
            onChange={(e) => setFormData(prev => ({ ...prev, option1: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Team A"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Option 2
          </label>
          <input
            type="text"
            value={formData.option2}
            onChange={(e) => setFormData(prev => ({ ...prev, option2: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Team B"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Duration
        </label>
        <select
          value={formData.duration}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            duration: parseInt(e.target.value)
          }))}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          required
        >
          {durations.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
          <input
            type="checkbox"
            checked={!!formData.incentiveType}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              incentiveType: e.target.checked ? 'BAI' : null,
              incentiveAmount: e.target.checked ? prev.incentiveAmount : ''
            }))}
            className="w-4 h-4 rounded border-gray-700 text-purple-600 focus:ring-purple-500"
          />
          Incentivize Prediction
        </label>

        {formData.incentiveType && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Currency
              </label>
              <select
                value={formData.incentiveType}
                onChange={(e) => setFormData(prev => ({ ...prev, incentiveType: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="BAI">BAI</option>
                <option value="USDT">USDT</option>
                <option value="BNB">BNB</option>
                <option value="IMX">IMX</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Amount
              </label>
              <input
                type="number"
                value={formData.incentiveAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, incentiveAmount: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={`Enter amount in ${formData.incentiveType}`}
                required={!!formData.incentiveType}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const PredictionRewards = ({ prediction }) => {
  if (!prediction?.incentiveType || !prediction?.incentiveAmount) return null;

  return (
    <div className="mt-2 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
      <div className="flex items-center justify-between text-sm">
        <span className="text-purple-400">Reward Pool:</span>
        <span className="font-medium text-white">
          {prediction.incentiveAmount} {prediction.incentiveType}
        </span>
      </div>
      <div className="mt-1 text-xs text-gray-400">
        Winners share the reward pool equally
      </div>
    </div>
  );
};

export default PredictionSection;
